import React, { useState, useCallback } from "react";
import {
  Card,
  Table,
  Tag,
  Row,
  Col,
  Statistic,
  Progress,
  DatePicker,
  Button,
  Space,
  Typography,
} from "antd";
import {
  FireOutlined,
  WarningOutlined,
  TeamOutlined,
  AppstoreOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import "../../../config";

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

const fmt = (d) => d.format("DD-MM-YYYY");

const SectionCard = ({ title, icon, iconColor, children, loading }) => (
  <Card
    size="small"
    loading={loading}
    title={
      <Space size={6}>
        {React.cloneElement(icon, { style: { color: iconColor, fontSize: 14 } })}
        <span style={{ fontSize: 13, fontWeight: 600 }}>{title}</span>
      </Space>
    }
    styles={{ body: { padding: "0 0 8px 0" } }}
    style={{ borderRadius: 10, border: "1px solid #f0f0f0" }}
  >
    {children}
  </Card>
);

const ProductInsightsDashboard = () => {
  const defaultRange = [dayjs().startOf("month"), dayjs().endOf("month")];
  const [dateRange, setDateRange] = useState(defaultRange);
  const [loading, setLoading] = useState(false);

  const [topViewed, setTopViewed] = useState([]);
  const [oosData, setOosData] = useState([]);
  const [reachData, setReachData] = useState([]);
  const [categoriesData, setCategoriesData] = useState([]);

  const handleLoad = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const base = global.config.APIURL.BaseURL;
      const from = fmt(dateRange[0]);
      const to = fmt(dateRange[1]);

      const [tvRes, oosRes, reachRes, catRes] = await Promise.all([
        axios.get(`${base}api/ActivityAnalytics/GetTopViewedProducts?From=${from}&To=${to}&Top=20`, { headers }),
        axios.get(`${base}api/ActivityAnalytics/GetOutOfStockDemand?From=${from}&To=${to}&Top=20`, { headers }),
        axios.get(`${base}api/ActivityAnalytics/GetUniqueUserReachPerProduct?From=${from}&To=${to}&Top=20`, { headers }),
        axios.get(`${base}api/ActivityAnalytics/GetTopBrowsedCategories?From=${from}&To=${to}&Top=10`, { headers }),
      ]);

      setTopViewed(tvRes.data || []);
      setOosData(oosRes.data || []);
      setReachData(reachRes.data || []);
      setCategoriesData(catRes.data || []);
    } catch {
      // silent — tables stay as-is
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  // Summary stat derivations
  const totalViews = topViewed.reduce((s, r) => s + r.ViewCount, 0);
  const oosSignals = oosData.reduce((s, r) => s + r.ViewsWhileOutOfStock, 0);
  const widestReach = reachData.length > 0 ? reachData[0].UniqueUserCount : 0;
  const topCategory = categoriesData.length > 0 ? categoriesData[0].CategoryName : "—";

  const maxCatViews = categoriesData.length > 0 ? categoriesData[0].ViewCount : 1;

  // ─── Top Viewed Columns ───────────────────────────────
  const topViewedCols = [
    {
      title: "#",
      key: "rank",
      width: 44,
      render: (_, __, i) => <Text type="secondary" style={{ fontSize: 12, whiteSpace: "nowrap" }}>{i + 1}</Text>,
    },
    {
      title: "Product",
      key: "product",
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13, color: "#141414" }}>{r.ProductName}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>{r.DesignNo || "—"}</Text>
        </div>
      ),
    },
    {
      title: "Rate",
      dataIndex: "SalesRate",
      width: 80,
      render: (v) => <Text style={{ fontSize: 12 }}>₹{v}</Text>,
    },
    {
      title: "Stock",
      dataIndex: "IsInStock",
      width: 80,
      render: (v) => <Tag color={v ? "success" : "default"} style={{ fontSize: 11 }}>{v ? "In Stock" : "Out"}</Tag>,
    },
    {
      title: "Views",
      dataIndex: "ViewCount",
      width: 70,
      render: (v) => <Tag color="blue" style={{ fontSize: 12, fontWeight: 600 }}>{v}</Tag>,
    },
  ];

  // ─── Out-of-Stock Demand Columns ─────────────────────
  const oosCols = [
    {
      title: "#",
      key: "rank",
      width: 44,
      render: (_, __, i) => <Text type="secondary" style={{ fontSize: 12, whiteSpace: "nowrap" }}>{i + 1}</Text>,
    },
    {
      title: "Product",
      key: "product",
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13, color: "#141414" }}>{r.ProductName}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>{r.DesignNo || "—"}</Text>
        </div>
      ),
    },
    {
      title: "Restocked?",
      dataIndex: "IsNowInStock",
      width: 90,
      render: (v) => <Tag color={v ? "success" : "error"} style={{ fontSize: 11 }}>{v ? "Yes" : "No"}</Tag>,
    },
    {
      title: "Demand Views",
      dataIndex: "ViewsWhileOutOfStock",
      width: 100,
      render: (v) => <Tag color="orange" style={{ fontSize: 12, fontWeight: 600 }}>{v}</Tag>,
    },
    {
      title: "Customers",
      dataIndex: "UniqueViewers",
      width: 90,
      render: (v) => <Tag color="red" style={{ fontSize: 12 }}>{v}</Tag>,
    },
  ];

  // ─── Unique Reach Columns ─────────────────────────────
  const reachCols = [
    {
      title: "#",
      key: "rank",
      width: 44,
      render: (_, __, i) => <Text type="secondary" style={{ fontSize: 12, whiteSpace: "nowrap" }}>{i + 1}</Text>,
    },
    {
      title: "Product",
      key: "product",
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13, color: "#141414" }}>{r.ProductName}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>{r.DesignNo || "—"}</Text>
        </div>
      ),
    },
    {
      title: "Customers",
      dataIndex: "UniqueUserCount",
      width: 90,
      render: (v) => <Tag color="purple" style={{ fontSize: 12, fontWeight: 600 }}>{v}</Tag>,
    },
    {
      title: "Total Views",
      dataIndex: "TotalViewCount",
      width: 90,
      render: (v) => <Tag color="default" style={{ fontSize: 12 }}>{v}</Tag>,
    },
    {
      title: "Avg / User",
      key: "avg",
      width: 80,
      render: (_, r) => (
        <Text style={{ fontSize: 12 }}>
          {r.UniqueUserCount > 0 ? (r.TotalViewCount / r.UniqueUserCount).toFixed(1) : "—"}
        </Text>
      ),
    },
  ];

  // ─── Category Columns ─────────────────────────────────
  const catCols = [
    {
      title: "#",
      key: "rank",
      width: 44,
      render: (_, __, i) => <Text type="secondary" style={{ fontSize: 12, whiteSpace: "nowrap" }}>{i + 1}</Text>,
    },
    {
      title: "Category",
      dataIndex: "CategoryName",
      render: (v) => <Text style={{ fontSize: 13, fontWeight: 600 }}>{v}</Text>,
    },
    {
      title: "Views",
      dataIndex: "ViewCount",
      width: 70,
      render: (v) => <Tag color="geekblue" style={{ fontSize: 12, fontWeight: 600 }}>{v}</Tag>,
    },
    {
      title: "Customers",
      dataIndex: "UniqueUsers",
      width: 90,
      render: (v) => <Tag color="cyan" style={{ fontSize: 12 }}>{v}</Tag>,
    },
    {
      title: "Share",
      key: "share",
      width: 110,
      render: (_, r) => (
        <Progress
          percent={Math.round((r.ViewCount / maxCatViews) * 100)}
          size="small"
          showInfo={false}
          strokeColor="#4096ff"
        />
      ),
    },
  ];

  const tableProps = {
    size: "small",
    pagination: false,
    scroll: { x: 500 },
  };

  return (
    <div style={{ background: "#fff", borderRadius: 12, padding: "20px 24px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <Title level={5} style={{ margin: 0, fontWeight: 700 }}>Product Insights</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>Customer engagement analytics from activity logs</Text>
        </div>
        <Space wrap size={8}>
          <RangePicker
            value={dateRange}
            onChange={(val) => val && setDateRange(val)}
            format="DD-MM-YYYY"
            size="small"
          />
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            size="small"
            loading={loading}
            onClick={handleLoad}
          >
            Load
          </Button>
        </Space>
      </div>

      {/* Summary Stats */}
      <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ borderRadius: 10, border: "1px solid #f0f0f0", background: "#f0f5ff" }}>
            <Statistic
              title={<Text style={{ fontSize: 11, color: "#595959" }}>Total Product Views</Text>}
              value={totalViews}
              valueStyle={{ fontSize: 22, fontWeight: 700, color: "#2f54eb" }}
              prefix={<FireOutlined style={{ fontSize: 14 }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ borderRadius: 10, border: "1px solid #fff1f0", background: "#fff7e6" }}>
            <Statistic
              title={<Text style={{ fontSize: 11, color: "#595959" }}>Out-of-Stock Signals</Text>}
              value={oosSignals}
              valueStyle={{ fontSize: 22, fontWeight: 700, color: "#fa8c16" }}
              prefix={<WarningOutlined style={{ fontSize: 14 }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ borderRadius: 10, border: "1px solid #f9f0ff", background: "#f9f0ff" }}>
            <Statistic
              title={<Text style={{ fontSize: 11, color: "#595959" }}>Widest Reach (1 Product)</Text>}
              value={widestReach}
              suffix="customers"
              valueStyle={{ fontSize: 22, fontWeight: 700, color: "#722ed1" }}
              prefix={<TeamOutlined style={{ fontSize: 14 }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ borderRadius: 10, border: "1px solid #f0f0f0", background: "#f6ffed" }}>
            <Statistic
              title={<Text style={{ fontSize: 11, color: "#595959" }}>Top Category</Text>}
              value={topCategory}
              valueStyle={{ fontSize: 16, fontWeight: 700, color: "#389e0d" }}
              prefix={<AppstoreOutlined style={{ fontSize: 14 }} />}
            />
          </Card>
        </Col>
      </Row>

      {/* Tables — Row 1 */}
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={24} lg={14}>
          <SectionCard title="Top Viewed Products" icon={<FireOutlined />} iconColor="#2f54eb" loading={loading}>
            <Table
              {...tableProps}
              dataSource={topViewed}
              columns={topViewedCols}
              rowKey="ProductID"
            />
          </SectionCard>
        </Col>
        <Col xs={24} lg={10}>
          <SectionCard title="Top Browsed Categories" icon={<AppstoreOutlined />} iconColor="#13c2c2" loading={loading}>
            <Table
              {...tableProps}
              dataSource={categoriesData}
              columns={catCols}
              rowKey="CategoryID"
            />
          </SectionCard>
        </Col>
      </Row>

      {/* Tables — Row 2 */}
      <Row gutter={[12, 12]}>
        <Col xs={24} lg={12}>
          <SectionCard title="Out-of-Stock Demand" icon={<WarningOutlined />} iconColor="#fa8c16" loading={loading}>
            <Table
              {...tableProps}
              dataSource={oosData}
              columns={oosCols}
              rowKey="ProductID"
            />
          </SectionCard>
        </Col>
        <Col xs={24} lg={12}>
          <SectionCard title="Unique Customer Reach" icon={<TeamOutlined />} iconColor="#722ed1" loading={loading}>
            <Table
              {...tableProps}
              dataSource={reachData}
              columns={reachCols}
              rowKey="ProductID"
            />
          </SectionCard>
        </Col>
      </Row>

      <style>{`
        .ant-table-thead > tr > th {
          font-size: 11px !important;
          font-weight: 600 !important;
          color: #8c8c8c !important;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          background: #fafafa !important;
          padding: 8px 12px !important;
        }
        .ant-table-tbody > tr > td {
          padding: 8px 12px !important;
          border-bottom: 1px solid #f5f5f5 !important;
        }
      `}</style>
    </div>
  );
};

export default ProductInsightsDashboard;
